import {
	Button,
	Drawer,
	DrawerCloseButton,
	DrawerContent,
	DrawerOverlay,
	Flex,
	Heading,
	Input,
	Text,
	useToast,
	Image,
	DrawerHeader,
	DrawerBody,
} from '@tempo/core';
import {useTranslation} from 'next-i18next';
import React from 'react';
import {useUpdateGroupSettingsMutation} from '@graphql/generated';
import Toast, {INTENT} from '@components/Toast';
import inviteFriendIllustration from '@assets/invite_friend_ big.svg';

import {useInvitationLink} from '@providers/InvitationLinkContext';

export type Group = {
	groupName: string;
	groupId: string;
};

interface ModifyGroupDrawerProps {
	onCloseModifyGroupDrawer: () => void;
	group: Group;
}

const ModifyGroupDrawer = ({
	onCloseModifyGroupDrawer,
	group,
}: ModifyGroupDrawerProps) => {
	const {t} = useTranslation();
	const {onOpenBottomSheetShare} = useInvitationLink();

	const [groupNameState, setGroupNameState] = React.useState<string>('');

	const [isModify, setIsModify] = React.useState<boolean>(true);

	const toast = useToast({
		duration: 3000,
	});

	const [mutationUpdateGroupName] = useUpdateGroupSettingsMutation({
		onCompleted: ({musicTogetherUpdateGroupSettings}) => {
			if (
				musicTogetherUpdateGroupSettings.__typename ===
				'MusicTogetherUpdateGroupSettingsError'
			) {
				return toast({
					render: () => (
						<Toast intent={INTENT.ERROR}>
							{t('errormessage_text_anerroroccurredpleasetryagain_web')}
						</Toast>
					),
				});
			}

			if (
				musicTogetherUpdateGroupSettings.__typename ===
				'MusicTogetherUpdateGroupSettingsOutput'
			) {
				setIsModify(false);
			}
		},
	});

	const updateGroupName = React.useCallback(
		(name: string) => {
			mutationUpdateGroupName({
				variables: {
					input: {
						musicTogetherGroupId: group.groupId,
						name: name,
					},
				},
			});
		},
		[group, mutationUpdateGroupName],
	);

	return (
		<Drawer isOpen={!!group} onClose={onCloseModifyGroupDrawer} size="full">
			<DrawerOverlay />
			<DrawerContent>
				<DrawerHeader>
					<Heading
						textAlign="center"
						variant="heading.xs"
						color="text.neutral.primary.default"
					>
						{t('musictogethernavigation_title_creategroup_web')}
					</Heading>
				</DrawerHeader>
				<DrawerCloseButton />
				<DrawerBody p="spacing.l">
					{!isModify && (
						<Image
							mx="auto"
							mb="spacing.2xl"
							src={inviteFriendIllustration.src}
							alt=""
						/>
					)}

					<Heading
						mb="spacing.l"
						variant="heading.xl"
						color="text.neutral.primary.default"
						textAlign="center"
					>
						{isModify
							? t('musictogethergroupcreation_title_chooseagroupname_web')
							: t('musictogethergroupcreation_title_inviteyourfriends_web')}
					</Heading>
					<Text
						variant="body.s.default"
						color="text.neutral.secondary.default"
						mb="spacing.2xl"
						textAlign="center"
					>
						{isModify
							? t('musictogethergroupcreation_text_validategroupname_web')
							: t(
									'musictogethergroupcreation_text_friendsjoingroupwiththislink_web',
								)}
					</Text>

					<Flex direction="column" margin="auto" w={['100%', '50%']}>
						{isModify ? (
							<>
								<Input
									placeholder={group.groupName}
									value={groupNameState}
									name="groupName"
									onChange={(e) => setGroupNameState(e.target.value)}
									maxLength={50}
									mb="spacing.xl"
									fontSize="md"
								/>
								<Button
									colorScheme="primary"
									onClick={() => updateGroupName(groupNameState)}
								>
									{t('generic_action_validate_web')}
								</Button>
							</>
						) : (
							<Button
								colorScheme="primary"
								onClick={() => onOpenBottomSheetShare(group.groupId)}
							>
								{t('musictogether_action_invitefriends_web')}
							</Button>
						)}
					</Flex>
				</DrawerBody>
			</DrawerContent>
		</Drawer>
	);
};

export default ModifyGroupDrawer;
